// Wizard flow state machine. Pure reducer so the consent and DFT-chaining
// logic is testable without DOM. The component drives side effects (API
// calls) off pendingSubmit / phase transitions.
export const initialFlowState = {
  phase: 'configuring', // configuring | awaiting_consent | dft_running | generating | complete | failed
  pendingSubmit: false,
  autoSubmitAfterDft: false,
  upgradedProps: null,
  jobId: null,
  progress: null,
  result: null,
  error: null,
};

export function latticeFlowReducer(state, action) {
  switch (action.type) {
    case 'GENERATE_CLICKED': {
      const { readiness, path } = action;
      if (readiness.ready) return { ...state, pendingSubmit: true, error: null };
      // dft_first is the mechanism for resolving missing (not just
      // estimate-grade) required props, so it must be allowed to proceed even
      // when readiness.missing is non-empty: that is exactly the "Run DFT,
      // then generate" CTA the wizard now surfaces for a missing state whose
      // gaps are DFT-derivable (see GateRow / DFT_DERIVABLE). The missing
      // check below stays as a backstop for the other two paths (ready /
      // estimates), which assume every required prop already has a value.
      if (path === 'dft_first') {
        return { ...state, phase: 'dft_running', autoSubmitAfterDft: true, error: null };
      }
      if (readiness.missing.length > 0) {
        return { ...state, error: `Missing properties: ${readiness.missing.join(', ')}` };
      }
      return { ...state, phase: 'awaiting_consent', pendingSubmit: false, error: null };
    }
    case 'CONSENT_GIVEN':
      return { ...state, phase: 'configuring', pendingSubmit: true };
    case 'CONSENT_DECLINED':
      return { ...state, phase: 'configuring', pendingSubmit: false };
    case 'DFT_STARTED':
      // Used when the wizard resumes a job it finds already in flight
      // (dftJobRegistry) on mount, rather than via the GENERATE_CLICKED
      // dft_first path (which sets autoSubmitAfterDft itself, above).
      // action.autoGenerate carries the ORIGINAL submitter's intent, read
      // back from the registry entry: true only when the job was registered
      // by this wizard's own "Run DFT, then generate" chain, false (the
      // default) for a job the Analyze panel submitted standalone. Without
      // this, every resume would either silently drop the "then generate"
      // chain (always false) or auto-fire a lattice run nobody asked for on
      // a panel-submitted job the wizard happens to adopt (always true) --
      // neither is safe to assume, so the flag must travel with the job.
      return { ...state, phase: 'dft_running', autoSubmitAfterDft: !!action.autoGenerate };
    case 'DFT_COMPLETE': {
      // D5 review fix (durability audit 2026-08-20, batch C review
      // IMPORTANT-2): the wizard's DFT resume and lattice-job resume mount
      // effects (LatticeWizard.jsx) run concurrently and independently on
      // reopen. A lattice-job resume (LATTICE_SUBMITTED, phase ->
      // 'generating') is a single Firestore read and typically lands first;
      // the DFT resume's own DFT_COMPLETE requires an extra poll round trip
      // to the DFT service and can arrive after. Once phase is already
      // 'generating' (a GA job for this candidate is genuinely in flight)
      // or 'complete' (it already finished), a DFT_COMPLETE that still
      // carries autoSubmitAfterDft from that same original "Run DFT, then
      // generate" chain must NEVER knock phase back to 'configuring' or set
      // pendingSubmit: doing so fired a second, duplicate, expensive GA job
      // against a candidate that already has one running or done. The
      // upgraded DFT values are still merged into upgradedProps for
      // consistency (harmless, and matches what a fresh render of the
      // property table would show), but phase/pendingSubmit/
      // autoSubmitAfterDft are left untouched.
      if (state.phase === 'generating' || state.phase === 'complete') {
        return { ...state, upgradedProps: action.props };
      }
      const next = { ...state, phase: 'configuring', upgradedProps: action.props };
      if (state.autoSubmitAfterDft) {
        // action.readiness is assessed by the caller over the upgraded prop
        // set (post-DFT values plus user edits). Defense in depth: whatever
        // the cause (the caller is expected to resolve DFT-vs-stale-edit
        // precedence before assessing this, but a required prop can still
        // legitimately come back missing, e.g. the DFT job doesn't supply
        // strength/elongation/toughness and no Young's modulus landed to
        // derive them from), DFT_COMPLETE must never auto-submit, or even
        // drop into the estimate-grade consent dialog, while a required prop
        // is still missing outright: that's a null in material_props, not
        // an estimate a user can knowingly proceed past. Route back to
        // configuring with the same "Missing properties" wording
        // GENERATE_CLICKED's own backstop uses, flag cleared.
        if (action.readiness && action.readiness.missing && action.readiness.missing.length > 0) {
          return {
            ...next,
            pendingSubmit: false,
            autoSubmitAfterDft: false,
            error: `Missing properties: ${action.readiness.missing.join(', ')}`,
          };
        }
        // If required props for this objective are still estimate-grade
        // after the DFT upgrade (e.g. impact/crush strength, elongation,
        // toughness, which the elastic tensor job never supplies), route
        // through the same consent gate the manual "Generate with
        // estimates" path uses instead of silently auto-submitting. Absent
        // readiness (older callers) keeps the prior always-auto-submit
        // behavior.
        if (action.readiness && action.readiness.estimateGrade && action.readiness.estimateGrade.length > 0) {
          return { ...next, phase: 'awaiting_consent', pendingSubmit: false, autoSubmitAfterDft: false };
        }
        return { ...next, pendingSubmit: true, autoSubmitAfterDft: false };
      }
      return next;
    }
    case 'DFT_FAILED':
      return { ...state, phase: 'configuring', autoSubmitAfterDft: false, error: action.error };
    case 'LATTICE_SUBMITTED':
      return { ...state, phase: 'generating', pendingSubmit: false, jobId: action.jobId, progress: null };
    case 'LATTICE_PROGRESS':
      return { ...state, progress: action.progress };
    case 'LATTICE_COMPLETE':
      return { ...state, phase: 'complete', result: action.result };
    case 'LATTICE_FAILED':
      return { ...state, phase: 'failed', error: action.error };
    case 'RESET':
      return { ...initialFlowState };
    default:
      return state;
  }
}
